import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Stack, Grid, CircularProgress, IconButton,
  Chip, LinearProgress, Button, Tooltip, Avatar,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  People as PeopleIcon,
  AccessTime as ClockIcon,
  LocalFireDepartment as FireIcon,
  PersonOff as UnassignedIcon,
  NotificationsActive as FollowUpIcon,
  CheckCircle as CheckIcon,
  WarningAmber as WarningIcon,
  RequestQuoteOutlined as QuoteIcon,
  TrendingUp as TrendingUpIcon,
  EventAvailable as BookingIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import HistoryIcon from "@mui/icons-material/History";
import {
  fetchDashboardStats, fetchLeadsWithMeta, fetchTeamUsers, reclassifyAllLeads,
  fetchMyActiveLeads, fetchPreviouslyAssignedLeads,
  type DashboardStats, type LeadThread, type TeamUser,
  STATUS_CONFIG, PIPELINE_ORDER,
} from "./leadApi";
import { leadConfig, showsVenueField, resolveInquirySubject } from "./leadVertical";

/**
 * Vertical-aware "subject" line for a lead card. Autism / tutoring
 * return the program (+ optional location); venue returns event_type
 * (+ optional event_date). Both fall back to em-dash when nothing
 * is available so the row keeps its rhythm.
 */
function leadSubjectLine(
  lead: LeadThread,
  opts: { withDate?: boolean; withGuests?: boolean } = {},
): string {
  if (leadConfig.supportsEventFields) {
    const parts: string[] = [];
    parts.push(lead.event_type?.replace(/_/g, " ") || "—");
    if (opts.withDate && showsVenueField("event_date") && (lead as any).event_date) {
      // Callers format the date themselves; here we just append the raw
      // for the simplest cases. Complex cards continue to format inline.
    }
    if (opts.withGuests && showsVenueField("guest_count") && lead.guest_count != null) {
      parts.push(`${lead.guest_count} guests`);
    }
    return parts.join(" · ");
  }
  const s = resolveInquirySubject(lead);
  return [s.program ?? "—", s.location].filter(Boolean).join(" · ");
}

const NAVY = "#16233A";
const GOLD = "#7C4DFF";
const SURFACE = {
  card:       "#ffffff",
  cardBorder: "1px solid #eef0f4",
  cardShadow: "0 1px 2px rgba(13,27,42,0.04), 0 4px 16px rgba(13,27,42,0.04)",
  cardRadius: "14px",
  divider:    "1px solid #f1f3f6",
} as const;

const todayStr = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
})();

/* ──────────────────────────────────────────────────────────────────────
   Lead Manager Dashboard — real-time sales operations cockpit.

   Replaces the prior analytics-heavy layout (conversion funnel /
   event-type / source / AI-score / status-breakdown charts) with
   operational widgets that answer the questions a sales manager
   actually asks when they sit down:

     • Who needs my attention right now?
     • Who is overdue?
     • Which staff are behind?
     • What's pending today?

   Data sources:
     • fetchDashboardStats — top KPI counters + by_status map.
     • fetchLeadsWithMeta — pulls live lead rows for the operational
       widgets (urgent leads, today's follow-ups, recent activity,
       upcoming bookings).
     • fetchTeamUsers — staff list for the team-performance widget.
   ────────────────────────────────────────────────────────────────────── */
export default function LeadDashboard() {
  const navigate = useNavigate();
  const [stats,        setStats]        = useState<DashboardStats | null>(null);
  const [urgentLeads,  setUrgentLeads]  = useState<LeadThread[]>([]);
  const [todayLeads,   setTodayLeads]   = useState<LeadThread[]>([]);
  const [recentLeads,  setRecentLeads]  = useState<LeadThread[]>([]);
  const [bookings,     setBookings]     = useState<LeadThread[]>([]);
  const [teamUsers,    setTeamUsers]    = useState<TeamUser[]>([]);
  // Assignment-aware buckets for the request user. Drives the two
  // sections required by the reassignment-visibility spec — "My
  // Active Leads" (currently held) and "Previously Assigned Leads"
  // (read-only historical access).
  const [myActive,        setMyActive]        = useState<LeadThread[]>([]);
  const [prevAssigned,    setPrevAssigned]    = useState<LeadThread[]>([]);
  const [prevAssignedTotal, setPrevAssignedTotal] = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [reclassifying, setReclassifying] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, urgent, today, recent, bookings, team, mine, prev] = await Promise.all([
        fetchDashboardStats(),
        // Urgent: follow-up overdue OR hot leads OR unassigned
        fetchLeadsWithMeta({ per_page: 8, sort: "follow_up_at", dir: "asc", follow_up_required: 1 }),
        fetchLeadsWithMeta({ per_page: 8, follow_up_today: 1, sort: "follow_up_at", dir: "asc" }),
        fetchLeadsWithMeta({ per_page: 10, sort: "created_at", dir: "desc" }),
        // Confirmed leads roll-up. Venue vertical sorts by upcoming event
        // date (most useful ordering when the lead IS a bookable event);
        // autism / tutoring have no event date so we fall back to created.
        fetchLeadsWithMeta({ per_page: 6, status: "confirmed", sort: leadConfig.supportsEventDate ? "event_date" : "created_at", dir: leadConfig.supportsEventDate ? "asc" : "desc" }),
        fetchTeamUsers().catch(() => [] as TeamUser[]),
        fetchMyActiveLeads({ per_page: 8 }).catch(() => ({ data: [], meta: { total: 0 } as any })),
        fetchPreviouslyAssignedLeads({ per_page: 6 }).catch(() => ({ data: [], meta: { total: 0 } as any })),
      ]);
      setStats(s);
      setUrgentLeads(urgent.data);
      setTodayLeads(today.data);
      setRecentLeads(recent.data);
      setBookings(bookings.data);
      setTeamUsers(team);
      setMyActive(mine.data);
      setPrevAssigned(prev.data);
      setPrevAssignedTotal(prev.meta?.total ?? prev.data.length);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── KPI calculations from the stats payload ─────────────────────────
  const overdue = useMemo(() => {
    // follow_up_required counts ALL leads that need follow-up; today's are
    // tracked separately. Anything in follow_up_required but not in
    // follow_up_today is past due (the lead's follow_up_at is in the past).
    if (!stats) return 0;
    return Math.max(0, (stats.follow_up_required || 0) - (stats.follow_up_today || 0));
  }, [stats]);

  const quotesPending = useMemo(() => {
    if (!stats) return 0;
    return (stats.by_status?.ready_for_quote || 0) + (stats.by_status?.ready_for_booking || 0);
  }, [stats]);

  const confirmedCount = stats?.by_status?.confirmed || 0;
  const newLeadsToday  = useMemo(() => {
    if (!stats) return 0;
    // The `recentLeads` array gives an exact count of leads created today;
    // fall back to the weekly counter if the recent list isn't loaded yet.
    return recentLeads.filter((l) => l.created_at?.startsWith(todayStr)).length;
  }, [stats, recentLeads]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  if (!stats) return null;

  // ── KPI definitions (status-accent only on the truly urgent cards) ──
  const kpis: KpiDef[] = [
    { id: "new",       label: "New Leads Today",  value: newLeadsToday,            sub: "Past 24h",          icon: <PeopleIcon sx={{ fontSize: 18 }} />,   accent: NAVY },
    { id: "followups", label: "Follow-ups Due",   value: stats.follow_up_today,    sub: "Today",             icon: <FollowUpIcon sx={{ fontSize: 18 }} />, accent: "#3b82f6" },
    { id: "overdue",   label: "Overdue Leads",    value: overdue,                  sub: "Past due follow-up",icon: <WarningIcon sx={{ fontSize: 18 }} />,  accent: "#dc2626" },
    { id: "hot",       label: "Hot Leads",        value: stats.hot_leads,          sub: "High intent",       icon: <FireIcon sx={{ fontSize: 18 }} />,     accent: "#f97316" },
    { id: "quotes",    label: "Quotes Pending",   value: quotesPending,            sub: "Quote / booking",   icon: <QuoteIcon sx={{ fontSize: 18 }} />,    accent: "#94a3b8" },
    { id: "bookings",  label: "Confirmed Bookings", value: confirmedCount,         sub: "Secured",           icon: <CheckIcon sx={{ fontSize: 18 }} />,    accent: "#16a34a" },
    { id: "unassigned",label: "Unassigned",       value: stats.unassigned,         sub: "Needs staff",       icon: <UnassignedIcon sx={{ fontSize: 18 }} />, accent: "#94a3b8" },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#faf8f4", minHeight: "100%" }}>
      {/* ── Header ───────────────────────────────────────────────────── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: NAVY, letterSpacing: "-0.3px" }}>
            Sales Operations
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", fontSize: 12.5 }}>
            Live command center · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small" variant="outlined"
            startIcon={reclassifying ? <CircularProgress size={14} /> : <AutoFixHighOutlinedIcon sx={{ fontSize: 16 }} />}
            disabled={reclassifying}
            onClick={async () => {
              setReclassifying(true);
              try { await reclassifyAllLeads(); loadData(); } catch {}
              setReclassifying(false);
            }}
            sx={{
              textTransform: "none", fontSize: 12, fontWeight: 600,
              borderColor: "#16a34a", color: "#16a34a", borderRadius: "8px",
              "&:hover": { bgcolor: "#f0fdf4", borderColor: "#16a34a" },
            }}
          >
            {reclassifying ? "Classifying…" : "AI Reclassify"}
          </Button>
          <IconButton
            onClick={loadData}
            sx={{
              border: SURFACE.cardBorder, borderRadius: "8px",
              "&:hover": { bgcolor: "#f8fafc" },
            }}
          >
            <RefreshIcon sx={{ fontSize: 18, color: "#475569" }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* ── KPI row — 7 compact operational tiles ───────────────────── */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(4, 1fr)",
          lg: "repeat(7, 1fr)",
        },
        gap: 1.2, mb: 2,
      }}>
        {kpis.map((k) => <KpiCard key={k.id} def={k} />)}
      </Box>

      {/* ── Main 70/30 grid ─────────────────────────────────────────── */}
      <Grid container spacing={1.6}>
        {/* ════ LEFT COLUMN ═══════════════════════════════════════════ */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={1.6}>
            {/* SECTION 1 · Leads Requiring Attention */}
            <SectionCard
              title="Leads Requiring Attention"
              subtitle={`${urgentLeads.length} priority items · overdue follow-ups, hot leads, unassigned`}
              accentIcon={<WarningIcon sx={{ fontSize: 16, color: "#dc2626" }} />}
              onSeeAll={() => navigate("/leads/inquiries?follow_up=1")}
            >
              {urgentLeads.length === 0 ? (
                <EmptyState
                  icon={<CheckIcon sx={{ fontSize: 28, color: "#16a34a" }} />}
                  text="No urgent items — pipeline is clear."
                />
              ) : (
                <Stack>
                  {urgentLeads.map((lead, i) => (
                    <UrgentLeadRow
                      key={lead.id}
                      lead={lead}
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      isLast={i === urgentLeads.length - 1}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>

            {/* SECTION 2 · Today's Follow-ups */}
            <SectionCard
              title="Today's Follow-ups"
              subtitle={`${todayLeads.length} scheduled · sorted by time`}
              accentIcon={<ClockIcon sx={{ fontSize: 16, color: "#3b82f6" }} />}
              onSeeAll={() => navigate("/leads/inquiries?follow_up_today=1")}
            >
              {todayLeads.length === 0 ? (
                <EmptyState
                  icon={<ClockIcon sx={{ fontSize: 28, color: "#94a3b8" }} />}
                  text="Nothing scheduled for today."
                />
              ) : (
                <Stack>
                  {todayLeads.map((lead, i) => (
                    <FollowUpRow
                      key={lead.id}
                      lead={lead}
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      isLast={i === todayLeads.length - 1}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>

            {/* SECTION · My Active Leads — leads currently assigned to me.
                Required by the reassignment-visibility spec. Backed by
                GET /leads/view/my-active so the count stays accurate
                even when the broader visibility scope would have
                surfaced unassigned / previously-held rows too. */}
            <SectionCard
              title="My Active Leads"
              subtitle={`${myActive.length} currently assigned to you`}
              accentIcon={<PeopleIcon sx={{ fontSize: 16, color: NAVY }} />}
              onSeeAll={() => navigate("/leads/inquiries?view=my")}
            >
              {myActive.length === 0 ? (
                <EmptyState
                  icon={<PeopleIcon sx={{ fontSize: 28, color: "#94a3b8" }} />}
                  text="You have no active leads assigned. Claim an unassigned lead from the intake pool."
                />
              ) : (
                <Stack>
                  {myActive.slice(0, 6).map((lead, i) => (
                    <ActivityRow
                      key={lead.id}
                      lead={lead}
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      isLast={i === Math.min(myActive.length - 1, 5)}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>

            {/* SECTION · Previously Assigned Leads — leads I used to own.
                Read-only on the detail page (the "Read Only – Previously
                Assigned" banner + disabled action buttons). Source of
                truth is lead_assignments_history.removed_at IS NOT NULL.
                The card is hidden entirely when the user has no history,
                to avoid cluttering the dashboard for new staff. */}
            {prevAssignedTotal > 0 && (
              <SectionCard
                title="Previously Assigned Leads"
                subtitle={`${prevAssignedTotal} read-only · leads you used to own`}
                accentIcon={<HistoryIcon sx={{ fontSize: 16, color: "#a16207" }} />}
                onSeeAll={() => navigate("/leads/inquiries?view=previously_assigned")}
              >
                <Stack>
                  {prevAssigned.slice(0, 5).map((lead, i) => (
                    <ActivityRow
                      key={lead.id}
                      lead={lead}
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      isLast={i === Math.min(prevAssigned.length - 1, 4)}
                    />
                  ))}
                </Stack>
              </SectionCard>
            )}

            {/* SECTION 3 · Recent Leads — newly created inquiries, NOT
                activity items. Renamed from "Recent Activity" so the
                term "Activity" stays exclusive to per-lead Timeline. */}
            <SectionCard
              title="Recent Leads"
              subtitle="Newest inquiries across the team"
              accentIcon={<TrendingUpIcon sx={{ fontSize: 16, color: "#94a3b8" }} />}
              onSeeAll={() => navigate("/leads/inquiries")}
            >
              {recentLeads.length === 0 ? (
                <EmptyState
                  icon={<TrendingUpIcon sx={{ fontSize: 28, color: "#94a3b8" }} />}
                  text="No leads yet."
                />
              ) : (
                <Stack>
                  {recentLeads.slice(0, 6).map((lead, i) => (
                    <ActivityRow
                      key={lead.id}
                      lead={lead}
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      isLast={i === Math.min(recentLeads.length - 1, 5)}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Stack>
        </Grid>

        {/* ════ RIGHT SIDEBAR ════════════════════════════════════════ */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={1.6}>
            {/* Pipeline Snapshot */}
            <SectionCard title="Pipeline Snapshot" subtitle="Stage distribution">
              <Stack spacing={1.3} sx={{ pt: 0.5 }}>
                {PIPELINE_ORDER.filter((s) => s !== "lost").map((s) => {
                  const cfg   = STATUS_CONFIG[s];
                  const count = stats.by_status[s] || 0;
                  const total = PIPELINE_ORDER.reduce((sum, k) => sum + (stats.by_status[k] || 0), 0);
                  const pct   = total > 0 ? (count / total) * 100 : 0;
                  return (
                    <Box key={s}>
                      <Stack direction="row" justifyContent="space-between" mb={0.5}>
                        <Stack direction="row" alignItems="center" gap={0.6}>
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: cfg.color }} />
                          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: NAVY }}>
                            {cfg.label}
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: cfg.color, fontVariantNumeric: "tabular-nums" }}>
                          {count}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate" value={pct}
                        sx={{
                          height: 5, borderRadius: 3,
                          bgcolor: "#f1f5f9",
                          "& .MuiLinearProgress-bar": { bgcolor: cfg.color, borderRadius: 3 },
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </SectionCard>

            {/* Team Performance */}
            <SectionCard
              title="Team Performance"
              subtitle={`${teamUsers.length} reps · sorted by active workload`}
              onSeeAll={() => navigate("/leads/inquiries")}
            >
              {teamUsers.length === 0 ? (
                <EmptyState
                  icon={<PeopleIcon sx={{ fontSize: 28, color: "#94a3b8" }} />}
                  text="No team data yet."
                />
              ) : (
                <Stack>
                  {teamUsers
                    .slice(0, 6)
                    .map((u, i) => (
                      <TeamRow
                        key={u.id}
                        user={u}
                        // No per-user lead count from API; the recent
                        // leads array gives a rough realtime indicator.
                        leadCount={recentLeads.filter((l) => l.assigned_to === u.id).length}
                        onClick={() => navigate(`/leads/inquiries?assigned_to=${u.id}`)}
                        isLast={i === Math.min(teamUsers.length - 1, 5)}
                      />
                    ))}
                </Stack>
              )}
            </SectionCard>

            {/* Upcoming Bookings */}
            <SectionCard
              title="Upcoming Bookings"
              subtitle={`${bookings.length} confirmed · sorted by event date`}
              accentIcon={<BookingIcon sx={{ fontSize: 16, color: "#16a34a" }} />}
              onSeeAll={() => navigate("/leads/inquiries?status=confirmed")}
            >
              {bookings.length === 0 ? (
                <EmptyState
                  icon={<BookingIcon sx={{ fontSize: 28, color: "#94a3b8" }} />}
                  text="No confirmed bookings yet."
                />
              ) : (
                <Stack>
                  {bookings.map((lead, i) => (
                    <BookingRow
                      key={lead.id}
                      lead={lead}
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      isLast={i === bookings.length - 1}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

/* ═════════════════════════════════════════════════════════════════════
   Sub-components — small operational tiles & row renderers.
   ═════════════════════════════════════════════════════════════════════ */

interface KpiDef {
  id: string;
  label: string;
  value: number | string;
  sub: string;
  icon: React.ReactNode;
  /** Status accent — only urgent / hot / confirmed cards use a vivid color. */
  accent: string;
}

function KpiCard({ def }: { def: KpiDef }) {
  return (
    <Paper elevation={0} sx={{
      px: 1.6, py: 1.4,
      borderRadius: SURFACE.cardRadius,
      border: SURFACE.cardBorder,
      bgcolor: SURFACE.card,
      boxShadow: SURFACE.cardShadow,
      transition: "all 0.18s",
      "&:hover": { transform: "translateY(-1px)", boxShadow: "0 6px 18px rgba(13,27,42,0.06)" },
    }}>
      <Stack direction="row" alignItems="center" gap={1.2}>
        <Box sx={{
          width: 32, height: 32, borderRadius: "8px",
          bgcolor: `${def.accent}14`, color: def.accent,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          {def.icon}
        </Box>
        <Box minWidth={0}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: NAVY, lineHeight: 1, letterSpacing: "-0.4px", fontVariantNumeric: "tabular-nums" }}>
            {def.value}
          </Typography>
          <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, textTransform: "uppercase", mt: 0.4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {def.label}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function SectionCard({
  title, subtitle, accentIcon, children, onSeeAll,
}: {
  title: string;
  subtitle?: string;
  accentIcon?: React.ReactNode;
  children: React.ReactNode;
  onSeeAll?: () => void;
}) {
  return (
    <Paper elevation={0} sx={{
      borderRadius: SURFACE.cardRadius,
      border: SURFACE.cardBorder,
      bgcolor: SURFACE.card,
      boxShadow: SURFACE.cardShadow,
      overflow: "hidden",
    }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1.4, borderBottom: SURFACE.divider }}>
        {accentIcon}
        <Box flex={1} minWidth={0}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: NAVY, letterSpacing: 0.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 0.1 }} noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
        {onSeeAll && (
          <Button
            size="small"
            onClick={onSeeAll}
            endIcon={<ChevronRightIcon sx={{ fontSize: "14px !important", ml: -0.5 }} />}
            sx={{
              textTransform: "none", fontSize: 11.5, fontWeight: 600,
              color: "#475569", minWidth: 0,
              "&:hover": { color: NAVY, bgcolor: "transparent" },
            }}
          >
            View all
          </Button>
        )}
      </Stack>
      <Box>{children}</Box>
    </Paper>
  );
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <Stack alignItems="center" justifyContent="center" gap={0.6} sx={{ py: 4 }}>
      {icon}
      <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{text}</Typography>
    </Stack>
  );
}

function StatusPill({ status }: { status: keyof typeof STATUS_CONFIG | string }) {
  const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];
  if (!cfg) return null;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.4,
      fontSize: 9, fontWeight: 700, color: cfg.color,
      bgcolor: "transparent", border: `1px solid ${cfg.color}55`,
      borderRadius: "5px", px: 0.55, py: 0.05,
      textTransform: "uppercase", letterSpacing: 0.4,
    }}>
      {cfg.label}
    </Box>
  );
}

function leadInitial(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

function avatarColor(seed: string): string {
  const palette = ["#3b82f6", "#8b5cf6", "#f59e0b", "#16a34a", "#dc2626", "#0ea5e9", "#a855f7", "#14b8a6"];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

function fmtRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return "—";
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1)   return "just now";
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)   return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtFollowUp(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!isFinite(d.getTime())) return "—";
  return d.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function fmtEventDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (!isFinite(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/* ── Lead row variants used inside the SectionCards ──────────────── */

function UrgentLeadRow({ lead, onClick, isLast }: { lead: LeadThread; onClick: () => void; isLast: boolean }) {
  const isOverdue   = !!(lead.follow_up_required && lead.follow_up_at && lead.follow_up_at < todayStr);
  const isHot       = lead.ai_score === "hot";
  const isUnassigned = !lead.assigned_to;
  const reason: { label: string; color: string } =
    isOverdue     ? { label: "OVERDUE",    color: "#dc2626" }
    : isHot       ? { label: "HOT",        color: "#f97316" }
    : isUnassigned ? { label: "UNASSIGNED", color: "#94a3b8" }
                  : { label: "ATTENTION",  color: "#3b82f6" };

  return (
    <Stack
      direction="row" alignItems="center" gap={1.2}
      onClick={onClick}
      sx={{
        px: 2, py: 1.2,
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        transition: "background 0.12s",
        "&:hover": { bgcolor: "#fafbfc" },
      }}
    >
      <Box sx={{ width: 3, alignSelf: "stretch", borderRadius: "2px", bgcolor: reason.color }} />
      <Avatar sx={{
        width: 32, height: 32, fontSize: 12, fontWeight: 700,
        bgcolor: `${avatarColor(lead.name || "")}15`,
        color: avatarColor(lead.name || ""),
        flexShrink: 0,
      }}>
        {leadInitial(lead.name)}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" gap={0.7} flexWrap="wrap">
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, textTransform: "capitalize" }} noWrap>
            {lead.name?.toLowerCase()}
          </Typography>
          <Box sx={{
            fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
            bgcolor: `${reason.color}14`, color: reason.color,
            border: `1px solid ${reason.color}40`, borderRadius: "5px",
            px: 0.5, py: 0.05,
          }}>
            {reason.label}
          </Box>
          <StatusPill status={lead.status} />
        </Stack>
        <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.1, textTransform: "capitalize" }} noWrap>
          {leadSubjectLine(lead)}
          {leadConfig.supportsEventFields && showsVenueField("event_date") && lead.event_date
            ? ` · ${fmtEventDate(lead.event_date)}` : ""}
        </Typography>
      </Box>
      <Box textAlign="right" sx={{ flexShrink: 0 }}>
        <Typography sx={{ fontSize: 10.5, color: "#94a3b8", fontVariantNumeric: "tabular-nums" }}>
          {lead.follow_up_at ? fmtRelativeTime(lead.follow_up_at) : "no follow-up"}
        </Typography>
        <Typography sx={{ fontSize: 10, color: "#cbd5e1", mt: 0.1 }} noWrap>
          {lead.assignee
            ? ((lead.assignee as any).display_name || lead.assignee.username || "—")
            : "Unassigned"}
        </Typography>
      </Box>
      <ChevronRightIcon sx={{ fontSize: 16, color: "#cbd5e1", flexShrink: 0 }} />
    </Stack>
  );
}

function FollowUpRow({ lead, onClick, isLast }: { lead: LeadThread; onClick: () => void; isLast: boolean }) {
  const overdueByTime = !!(lead.follow_up_at && new Date(lead.follow_up_at).getTime() < Date.now());
  return (
    <Stack
      direction="row" alignItems="center" gap={1.2}
      onClick={onClick}
      sx={{
        px: 2, py: 1.1,
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        "&:hover": { bgcolor: "#fafbfc" },
      }}
    >
      <Box sx={{
        width: 36, height: 36, borderRadius: "8px",
        bgcolor: overdueByTime ? "#fef2f2" : "#eff6ff",
        color: overdueByTime ? "#dc2626" : "#3b82f6",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
        fontVariantNumeric: "tabular-nums",
      }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, lineHeight: 1 }}>
          {lead.follow_up_at ? fmtFollowUp(lead.follow_up_at).split(" ")[0] : "—"}
        </Typography>
        <Typography sx={{ fontSize: 8, fontWeight: 700, mt: 0.1 }}>
          {lead.follow_up_at ? fmtFollowUp(lead.follow_up_at).split(" ")[1] : ""}
        </Typography>
      </Box>
      <Box flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" gap={0.6} flexWrap="wrap">
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: NAVY, textTransform: "capitalize" }} noWrap>
            {lead.name?.toLowerCase()}
          </Typography>
          {overdueByTime && (
            <Box sx={{ fontSize: 9, fontWeight: 800, color: "#dc2626", border: "1px solid #fecaca", borderRadius: "5px", px: 0.45, py: 0.05, letterSpacing: 0.4 }}>
              OVERDUE
            </Box>
          )}
        </Stack>
        <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.1, textTransform: "capitalize" }} noWrap>
          {leadSubjectLine(lead)}
          {" · "}
          {lead.assignee
            ? ((lead.assignee as any).display_name || lead.assignee.username)
            : <span style={{ color: "#94a3b8" }}>Unassigned</span>}
        </Typography>
      </Box>
      <ChevronRightIcon sx={{ fontSize: 16, color: "#cbd5e1", flexShrink: 0 }} />
    </Stack>
  );
}

function ActivityRow({ lead, onClick, isLast }: { lead: LeadThread; onClick: () => void; isLast: boolean }) {
  const cfg = STATUS_CONFIG[lead.status];
  return (
    <Stack
      direction="row" alignItems="center" gap={1.2}
      onClick={onClick}
      sx={{
        px: 2, py: 0.9,
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        "&:hover": { bgcolor: "#fafbfc" },
      }}
    >
      <Box sx={{
        width: 24, height: 24, borderRadius: "50%",
        bgcolor: `${cfg?.color || "#94a3b8"}14`,
        color: cfg?.color || "#94a3b8",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg?.color || "#94a3b8" }} />
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontSize: 12, color: "#475569" }} noWrap>
          <Box component="span" sx={{ fontWeight: 700, color: NAVY, textTransform: "capitalize" }}>
            {lead.name?.toLowerCase()}
          </Box>
          {" · "}
          <Box component="span" sx={{ color: cfg?.color || "#94a3b8", fontWeight: 600 }}>
            {cfg?.label || lead.status}
          </Box>
          {(() => {
            const subj = leadSubjectLine(lead);
            return subj && subj !== "—" ? (
              <Box component="span" sx={{ color: "#94a3b8", textTransform: "capitalize" }}>
                {" · "}{subj}
              </Box>
            ) : null;
          })()}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 10.5, color: "#94a3b8", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
        {fmtRelativeTime(lead.created_at)}
      </Typography>
    </Stack>
  );
}

function TeamRow({ user, leadCount, onClick, isLast }: { user: TeamUser; leadCount: number; onClick: () => void; isLast: boolean }) {
  const name =
    (user as any).display_name ||
    [user.first_name, user.last_name].filter(Boolean).join(" ") ||
    user.username;
  return (
    <Stack
      direction="row" alignItems="center" gap={1.2}
      onClick={onClick}
      sx={{
        px: 2, py: 1,
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        "&:hover": { bgcolor: "#fafbfc" },
      }}
    >
      <Avatar sx={{
        width: 28, height: 28, fontSize: 11, fontWeight: 700,
        bgcolor: `${avatarColor(name)}15`,
        color: avatarColor(name),
        flexShrink: 0,
      }}>
        {leadInitial(name)}
      </Avatar>
      <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: NAVY }} noWrap>
        {name}
      </Typography>
      <Tooltip title={`${leadCount} recent lead${leadCount === 1 ? "" : "s"}`}>
        <Chip
          label={`${leadCount} ${leadCount === 1 ? "lead" : "leads"}`}
          size="small"
          sx={{
            height: 20, fontSize: 10.5, fontWeight: 700,
            bgcolor: leadCount > 0 ? "#eef2ff" : "#f8fafc",
            color: leadCount > 0 ? "#4338ca" : "#94a3b8",
            border: `1px solid ${leadCount > 0 ? "#c7d2fe" : "#e2e8f0"}`,
            borderRadius: "5px",
            "& .MuiChip-label": { px: 0.8 },
          }}
        />
      </Tooltip>
    </Stack>
  );
}

function BookingRow({ lead, onClick, isLast }: { lead: LeadThread; onClick: () => void; isLast: boolean }) {
  // Budget value is a venue-vertical concept. Autism / tutoring don't
  // capture a dollar figure per lead, so the currency column collapses
  // to em-dash there and the row still reads as a confirmed-lead card.
  const value = showsVenueField("budget") ? Number(lead.budget_value ?? 0) : 0;
  return (
    <Stack
      direction="row" alignItems="center" gap={1.2}
      onClick={onClick}
      sx={{
        px: 2, py: 1.1,
        borderBottom: isLast ? "none" : SURFACE.divider,
        cursor: "pointer",
        "&:hover": { bgcolor: "#fafbfc" },
      }}
    >
      <Box sx={{
        width: 36, height: 36, borderRadius: "8px",
        bgcolor: "#f0fdf4", color: "#16a34a",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <BookingIcon sx={{ fontSize: 16 }} />
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: NAVY, textTransform: "capitalize" }} noWrap>
          {lead.name?.toLowerCase()}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: "#64748b", mt: 0.1, textTransform: "capitalize" }} noWrap>
          {leadSubjectLine(lead, { withGuests: true })}
          {leadConfig.supportsEventFields && showsVenueField("event_date") && lead.event_date
            ? ` · ${fmtEventDate(lead.event_date)}` : ""}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#16a34a", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
        {value > 0 ? `$${value.toLocaleString()}` : "—"}
      </Typography>
    </Stack>
  );
}
